import React, { useState, useEffect } from "react";
import {
  UserMenu,
  MenuItemLink,
  AppBar,
  Layout,
  Logout,
  useSidebarState,
  usePermissions,
} from "react-admin";
import { ProfileProvider } from "../pages/ProfileEdit";
import SettingsIcon from "@mui/icons-material/Settings";
import "./styles.css";
import {
  Box,
  Typography,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Button,
  DialogActions,
  DialogContentText,
  DialogContent,
  DialogTitle,
  Dialog,
  FormControlLabel,
  Switch,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Dashboard,
  DashboardCustomize,
  ExpandLess,
  ExpandMore,
  Home,
  Person as PersonIcon,
} from "@mui/icons-material";
import Group from "@mui/icons-material/Group";
import Psychology from "@mui/icons-material/Psychology";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SecurityIcon from "@mui/icons-material/Security";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_BASE } from "../providers/env";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { chatApi, userApi } from "../providers/env";
import { CircularProgress } from "@mui/material";
import { useCustomPermissions } from '../hooks/usePermissions';
import { PermissionGuard, SuperAdminGuard, RoleGuard, AnyPermissionGuard } from './PermissionGuard';

const resourceNames = [
  "dashboard",
  "agents",
  "users",
  "register",
  "my-profile",
  "context",
  "customer-data",
  "unanswered-question",
  "environ-variables",
  "vendor-api-master",
  "apiSettings",
  "config-settings",
  "zalo-config-settings",
  "whatsapp-config-settings",
  "tawkto-config-settings",
  "line-config-settings",
  "chat-transfer-cases",
];

const extractAgentIdFromPath = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return null;

  const last = parts[parts.length - 1];
  if (!resourceNames.includes(last)) {
    return last;
  }

  for (let i = 0; i < parts.length - 1; i++) {
    if (
      resourceNames.includes(parts[i]) &&
      !resourceNames.includes(parts[i + 1])
    ) {
      return parts[i + 1];
    }
  }

  return null;
};

const MyUserMenu = (props: any) => {
  return (
    <UserMenu {...props}>
      <MenuItemLink
        to="/my-profile"
        primaryText="My Profile"
        leftIcon={<SettingsIcon />}
      />
      <Logout key="logout" />
    </UserMenu>
  );
};

const MyAppBar = (props: any) => {
  const navigate = useNavigate();
  const [conversationsAnchor, setConversationsAnchor] =
    useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [saveChats, setSaveChats] = useState(true);
  const [loading, setLoading] = useState(false);
  const conversationsOpen = Boolean(conversationsAnchor);
  const { permissions: raPermissions } = usePermissions();
  const { hasPermission, isSuperAdmin, isOrgAdmin, isSubAdmin } = useCustomPermissions();

  // Agent ID ko get karo localStorage se
  const agentId = (() => {
    try {
      return localStorage.getItem("agentId");
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const fetchPermission = async () => {
      try {
        // Get agentId from localStorage
        const agentId = localStorage.getItem("agentId");

        if (!agentId) {
          setLoading(false);
          return;
        }

        const response = await userApi.getChatPermission({ agentId });
        setSaveChats(response.data.save_chat);
      } catch (error) {
        console.error("Error fetching chat permission:", error);
      }
    };
    fetchPermission();
  }, []);

  const handleConversationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setConversationsAnchor(event.currentTarget);
  };

  const handleConversationsClose = () => {
    setConversationsAnchor(null);
  };

  const handleViewAll = () => {
    navigate("/chats");
    handleConversationsClose();
  };

  const handleDeleteAll = () => {
    setDeleteDialogOpen(true);
    handleConversationsClose();
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const agentId = localStorage.getItem("agentId");

      if (!agentId) {
        alert("No agent selected");
        setLoading(false);
        return;
      }

      await chatApi.deleteAllChats({ agentId });
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handlePermissions = () => {
    setPermissionsDialogOpen(true);
    handleConversationsClose();
  };

  const handlePermissionsSave = async () => {
    setLoading(true);
    try {
      const agentId = localStorage.getItem("agentId");

      if (!agentId) {
        alert("No agent selected");
        setLoading(false);
        return;
      }

      await userApi.updateChatPermission({ saveChat: saveChats, agentId });
      setPermissionsDialogOpen(false);
    } catch (error) {
      console.error("Error saving permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionsCancel = () => {
    setPermissionsDialogOpen(false);
  };

  return (
    <AppBar {...props} userMenu={<MyUserMenu />}>
      <Typography variant="h6" sx={{ flex: 1 }} />
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mr: 2, gap: 2 }}>
          {/* Agents Link - Show if has agent view permission */}
          <PermissionGuard resource="agent" action="view">
            <Link
              href={`/agents`}
              rel="noopener noreferrer"
              underline="none"
              sx={{ display: "flex", alignItems: "center", color: "inherit" }}
            >
              <Psychology fontSize="small" sx={{ mr: 0.5 }} />
              Agents
            </Link>
          </PermissionGuard>

          {/* Users Link - Show only for Sub Admin, Org Admin, or Super Admin */}
          {/* <RoleGuard roles={['Org Admin', 'Sub Admin']}>
            <Link
              href={"/users"}
              rel="noopener noreferrer"
              underline="none"
              sx={{ display: "flex", alignItems: "center", color: "inherit" }}
            >
              <Group fontSize="small" sx={{ mr: 0.5 }} />
              Users
            </Link>
          </RoleGuard> */}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mr: 2, gap: 2 }}>
          {/* Conversations Menu - Show if has chat permissions */}
          {agentId && hasPermission('chat', 'view') && (
            <>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={handleConversationsClick}
                  sx={{
                    color: "inherit",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    padding: "4px 8px",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                  <Box component="span" sx={{ fontSize: "0.875rem" }}>
                    Conversations
                  </Box>
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>

                <Menu
                  anchorEl={conversationsAnchor}
                  open={conversationsOpen}
                  onClose={handleConversationsClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <MenuItem onClick={handleViewAll}>
                    <ListItemIcon>
                      <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View All</ListItemText>
                  </MenuItem>

                  {/* Delete All - Only if has delete permission */}
                  {hasPermission('chat', 'delete') && (
                    <MenuItem onClick={handleDeleteAll}>
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Delete All</ListItemText>
                    </MenuItem>
                  )}

                  {/* Permissions - Only for admins */}
                  {(isOrgAdmin() || isSubAdmin() || isSuperAdmin()) && (
                    <MenuItem onClick={handlePermissions}>
                      <ListItemIcon>
                        <LockIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Permissions</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Box>

              {/* Delete Confirmation Dialog */}
              <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    minWidth: "400px",
                  },
                }}
              >
                <DialogTitle
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <WarningAmberIcon color="warning" />
                  Delete All Conversations
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete all chats? This action cannot
                    be undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                  <Button onClick={handleDeleteCancel} variant="outlined">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteConfirm}
                    variant="contained"
                    color="error"
                    autoFocus
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    Delete All
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Permissions Dialog */}
              <Dialog
                open={permissionsDialogOpen}
                onClose={handlePermissionsCancel}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    minWidth: "400px",
                  },
                }}
              >
                <DialogTitle
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <LockIcon color="primary" />
                  Conversation Permissions
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ py: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={saveChats}
                          onChange={(e) => setSaveChats(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Save Chats
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable to save conversation history
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                  <Button onClick={handlePermissionsCancel} variant="outlined">
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePermissionsSave}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    Save
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          <Link
            href={`${API_BASE}/logs/`}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{ display: "flex", alignItems: "center", color: "inherit" }}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} />
            Logs
          </Link>
        </Box>
      </Box>
    </AppBar>
  );
};

export const MyMenu = () => {
  const [open] = useSidebarState();
  const [openContext, setOpenContext] = useState(true);
  const [openApi, setOpenApi] = useState(true);
  const [openSettings, setOpenSettings] = useState(true);
  const { permissions } = usePermissions();
  const { hasPermission, isSuperAdmin, isOrgAdmin, isSubAdmin } = useCustomPermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const agentName = (() => {
    try {
      return localStorage.getItem("agentName");
    } catch {
      return undefined;
    }
  })();

  const storedAgentId = (() => {
    try {
      return localStorage.getItem("agentId");
    } catch (e) {
      return null;
    }
  })();

  const pathAgentId = extractAgentIdFromPath(location.pathname);
  const agentId = storedAgentId || pathAgentId;

  const withAgent = (base: string) =>
    agentId ? `/${base}/${agentId}` : `/agents`;

  const subtleIfNoAgent = (sx: any = {}) =>
    agentId ? sx : { ...sx, opacity: 0.6 };

  // Check if user has any context-related permissions
  const hasAnyContextPermission =
    hasPermission('context', 'view') ||
    hasPermission('customer_data', 'view') ||
    hasPermission('unanswered_question', 'view');

  // Check if user has any API management permissions
  const hasAnyApiPermission =
    hasPermission('environ_variables', 'view') ||
    hasPermission('vendor_api', 'view') ||
    hasPermission('api_management', 'view');

  // Check if user has any settings permissions
  const hasAnySettingsPermission =
    hasPermission('config_settings', 'view') ||
    hasPermission('livechat_settings', 'view') ||
    hasPermission('tawkto_settings', 'view') ||
    hasPermission('whatsapp_settings', 'view') ||
    hasPermission('zalo_settings', 'view') ||
    hasPermission('line_settings', 'view') ||
    hasPermission('transfer_case', 'view');

  return (
    <Box sx={{ backgroundColor: "#fff", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          padding: "8px",
          width: "240px",
          paddingBottom: 0,
        }}
      >
        <IconButton
          onClick={() => {
            navigate(`/agents`);
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ flex: 1, width: "50%" }}>
          <Typography
            sx={{
              fontSize: "20px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              fontWeight: 600,
            }}
          >
            {agentName}
          </Typography>
        </Box>
      </Box>

      <List
        dense
        sx={{
          "& .MuiListItem-root": { px: 2, py: 1 },
          "& .MuiListItemText-primary": { fontSize: 16 },
        }}
      >
        {/* Dashboard - Show if has permission */}
        <PermissionGuard resource="dashboard" action="view">
          <MenuItemLink
            to={agentId ? `/dashboard/${agentId}` : `/dashboard`}
            primaryText="Dashboard"
            leftIcon={
              <ListItemIcon style={{ minWidth: 30 }}>
                <Home fontSize="small" />
              </ListItemIcon>
            }
            sidebarIsOpen={open}
            sx={{ fontSize: 16, pl: 2 }}
          />
        </PermissionGuard>

        {/* Context Settings - Show if has any context permission */}
        {hasAnyContextPermission && (
          <>
            <ListItemButton
              onClick={() => setOpenContext(!openContext)}
              sx={{ pl: 2 }}
            >
              <ListItemIcon style={{ minWidth: 30 }}>
                <Dashboard fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Context Settings"
                primaryTypographyProps={{ fontSize: 16 }}
              />
              {openContext ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </ListItemButton>

            <Collapse in={openContext} timeout="auto" unmountOnExit>
              <List disablePadding>
                {/* Context */}
                <PermissionGuard resource="context" action="view">
                  <MenuItemLink
                    to={withAgent("context")}
                    primaryText="Context"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                {/* Dynamic Contexts (Customer Data) */}
                <PermissionGuard resource="customer_data" action="view">
                  <MenuItemLink
                    to={withAgent("customer-data")}
                    primaryText="Dynamic Contexts"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                {/* Questions */}
                <PermissionGuard resource="unanswered_question" action="view">
                  <MenuItemLink
                    to={withAgent("unanswered-question")}
                    primaryText="Questions"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>
              </List>
            </Collapse>
          </>
        )}

        {/* API Management - Show if has any API permission */}
        {hasAnyApiPermission && (
          <>
            <ListItemButton onClick={() => setOpenApi(!openApi)} sx={{ pl: 2 }}>
              <ListItemIcon style={{ minWidth: 30 }}>
                <DashboardCustomize fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="API Management"
                primaryTypographyProps={{ fontSize: 16 }}
              />
              {openApi ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </ListItemButton>

            <Collapse in={openApi} timeout="auto" unmountOnExit>
              <List disablePadding>
                {/* API Variables */}
                <PermissionGuard resource="environ_variables" action="view">
                  <MenuItemLink
                    to={withAgent("environ-variables")}
                    primaryText="API Variables"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                {/* Test APIs */}
                <PermissionGuard resource="api_management" action="view">
                  <MenuItemLink
                    to={withAgent("apiSettings")}
                    primaryText="Test APIs"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                {/* APIs (Vendor API) */}
                <PermissionGuard resource="vendor_api" action="view">
                  <MenuItemLink
                    to={withAgent("vendor-api-master")}
                    primaryText="APIs"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>
              </List>
            </Collapse>
          </>
        )}

        {/* Settings - Show if has any settings permission */}
        {hasAnySettingsPermission && (
          <>
            <ListItemButton
              onClick={() => setOpenSettings(!openSettings)}
              sx={{ pl: 2 }}
            >
              <ListItemIcon style={{ minWidth: 30 }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{ fontSize: 16 }}
              />
              {openSettings ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </ListItemButton>

            <Collapse in={openSettings} timeout="auto" unmountOnExit>
              <List disablePadding>
                <PermissionGuard resource="livechat_settings" action="view">
                  <MenuItemLink
                    to={withAgent("config-settings")}
                    primaryText="Live Chat Settings"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                <PermissionGuard resource="tawkto_settings" action="view">
                  <MenuItemLink
                    to={withAgent("tawkto-config-settings")}
                    primaryText="TawkTo Settings"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                <PermissionGuard resource="whatsapp_settings" action="view">
                  <MenuItemLink
                    to={withAgent("whatsapp-config-settings")}
                    primaryText="Whatsapp Settings"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                <PermissionGuard resource="zalo_settings" action="view">
                  <MenuItemLink
                    to={withAgent("zalo-config-settings")}
                    primaryText="Zalo Settings"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                <PermissionGuard resource="line_settings" action="view">
                  <MenuItemLink
                    to={withAgent("line-config-settings")}
                    primaryText="Line Settings"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>

                <PermissionGuard resource="transfer_case" action="view">
                  <MenuItemLink
                    to={withAgent("chat-transfer-cases")}
                    primaryText="Transfer Case"
                    sidebarIsOpen={open}
                    sx={{ fontSize: 16, pl: 7, ...subtleIfNoAgent() }}
                  />
                </PermissionGuard>
              </List>
            </Collapse>
          </>
        )}

        {/* Invoice - Show to everyone for now, or add permission check */}
        <MenuItemLink
          to="/invoice"
          primaryText="Invoice"
          leftIcon={
            <ListItemIcon style={{ minWidth: 30 }}>
              <ReceiptIcon fontSize="small" />
            </ListItemIcon>
          }
          sidebarIsOpen={open}
          sx={{ fontSize: 16, pl: 2 }}
        />

        {/* Roles & Permissions - Show if user can manage users OR roles */}
        <AnyPermissionGuard
          permissions={[
            { resource: 'user', action: 'view' },
            { resource: 'role', action: 'view' },
          ]}
        >
          <MenuItemLink
            to="/roles-and-permissions"
            primaryText="Roles & Permissions"
            leftIcon={
              <ListItemIcon style={{ minWidth: 30 }}>
                <SecurityIcon fontSize="small" />
              </ListItemIcon>
            }
            sidebarIsOpen={open}
            sx={{ fontSize: 16, pl: 2 }}
          />
        </AnyPermissionGuard>

        <Logout
          sx={{
            "& .MuiListItemIcon-root": {
              minWidth: 30,
            },
          }}
          key="logout"
          icon={<LogoutIcon sx={{ fontSize: 30 }} />}
        />
      </List>
    </Box>
  );
};

const SwitchBackBanner = () => {
  const superAdminToken = localStorage.getItem("super_admin_token");
  if (!superAdminToken) return null;

  const handleSwitchBack = () => {
    const savedToken = localStorage.getItem("super_admin_token") || "";
    const savedPermissions = localStorage.getItem("super_admin_permissions") || "";

    // Restore super admin session
    localStorage.setItem("token", savedToken);
    if (savedPermissions) {
      localStorage.setItem("permissions", savedPermissions);
    }

    // Clean up impersonation keys
    localStorage.removeItem("super_admin_token");
    localStorage.removeItem("super_admin_permissions");
    localStorage.removeItem("organization_id");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("agentId");
    localStorage.removeItem("agentName");

    window.location.href = "/super-admin/dashboard";
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        backgroundColor: "#ff9800",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        px: 2,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        fontSize: "0.8rem",
        fontWeight: 500,
        zIndex: 9999,
      }}
    >
      Viewing as Org Admin
      <Button
        size="small"
        variant="contained"
        onClick={handleSwitchBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
        sx={{
          backgroundColor: "#fff",
          color: "#ff9800",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.75rem",
          py: 0.25,
          px: 1.5,
          minHeight: 0,
          "&:hover": { backgroundColor: "#fff3e0" },
        }}
      >
        Switch Back
      </Button>
    </Box>
  );
};

const MyLayout = (props: any) => {
  const location = useLocation();
  const hideMenu =
    location.pathname.startsWith("/agents") ||
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/users/create") ||
    location.pathname.startsWith("/my-profile") ||
    location.pathname.startsWith("/chats");

  // Hide react-admin AppBar and Menu for super-admin routes (they have their own layout)
  const isSuperAdminRoute = location.pathname.startsWith("/super-admin");

  return (
    <ProfileProvider>
      <SwitchBackBanner />
      <Layout
        {...props}
        appBar={isSuperAdminRoute ? () => null : MyAppBar}
        menu={hideMenu || isSuperAdminRoute ? () => null : MyMenu}
      />
    </ProfileProvider>
  );
};

export default MyLayout;
